import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FiFile, FiClock, FiRefreshCw, FiCheck, FiAlertTriangle } from 'react-icons/fi';
import { useTheme } from '../../../../contexts/ThemeContext';

const FileNode: React.FC<NodeProps> = ({ data, selected }) => {
  const { darkMode } = useTheme();

  const getStatusIcon = () => {
    switch (data.status) {
      case 'ghost': return <FiFile className="text-gray-400" />;
      case 'pending': return <FiClock className="text-yellow-500" />;
      case 'hydrating': return <FiRefreshCw className="text-blue-500 animate-spin" />;
      case 'fresh': return <FiCheck className="text-green-500" />;
      case 'stale': return <FiAlertTriangle className="text-orange-500" />;
      default: return <FiFile className="text-gray-400" />;
    }
  };

  const getNodeBgColor = () => {
    if (data.isFocusNode) {
      return darkMode ? 'bg-blue-900' : 'bg-blue-100';
    }
    if (data.isCompleted) {
      return darkMode ? 'bg-green-900' : 'bg-green-100';
    }
    
    // Add status-based background colors
    switch (data.status) {
      case 'ghost': return darkMode ? 'bg-gray-800' : 'bg-gray-100';
      case 'pending': return darkMode ? 'bg-yellow-900' : 'bg-yellow-100';
      case 'hydrating': return darkMode ? 'bg-blue-900' : 'bg-blue-100';
      case 'fresh': return darkMode ? 'bg-green-900' : 'bg-green-100';
      case 'stale': return darkMode ? 'bg-orange-900' : 'bg-orange-100';
      default: return darkMode ? 'bg-gray-800' : 'bg-white';
    }
  };

  const getNodeBorderColor = () => {
    // Special styling for root nodes in dependency groups
    if (data.isRootNode) {
      return 'border-purple-500 border-dashed';
    }
    if (data.isFocusNode) {
      return 'border-blue-500';
    }
    if (data.isCompleted) {
      return 'border-green-500';
    }
    switch (data.status) {
      case 'ghost': return darkMode ? 'border-gray-600' : 'border-gray-300';
      case 'pending': return 'border-yellow-500';
      case 'hydrating': return 'border-blue-500';
      case 'fresh': return 'border-green-500';
      case 'stale': return 'border-orange-500';
      default: return darkMode ? 'border-gray-600' : 'border-gray-300';
    }
  };
  
  const getHandleColor = (status: string): string => {
    switch (status) {
      case 'ghost': return darkMode ? '#6B7280' : '#9CA3AF';
      case 'pending': return '#F59E0B';
      case 'hydrating': return '#3B82F6';
      case 'fresh': return '#10B981';
      case 'stale': return '#F97316';
      case 'locked': return '#EF4444';
      default: return darkMode ? '#6B7280' : '#9CA3AF';
    }
  };

  // Helper function to get status badge class
  const getStatusBadgeClass = (status: string, darkMode: boolean): string => {
    switch (status) {
      case 'ghost':
        return darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700';
      case 'pending':
        return darkMode ? 'bg-yellow-800 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
      case 'hydrating':
        return darkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-100 text-blue-800';
      case 'fresh':
        return darkMode ? 'bg-green-800 text-green-300' : 'bg-green-100 text-green-800';
      case 'stale':
        return darkMode ? 'bg-orange-800 text-orange-300' : 'bg-orange-100 text-orange-800';
      case 'locked':
        return darkMode ? 'bg-red-800 text-red-300' : 'bg-red-100 text-red-800';
      default:
        return darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700';
    }
  };

  // Determine handle positions based on layout direction
  const sourcePosition = data.layoutDirection === 'BT' ? Position.Top : Position.Bottom;
  const targetPosition = data.layoutDirection === 'BT' ? Position.Bottom : Position.Top;

  return (
    <div className={`px-4 py-2 rounded-md shadow-md border-2 ${getNodeBgColor()} ${getNodeBorderColor()} transition-all duration-200 ${
      selected ? 'transform scale-110 shadow-lg z-10 ring-2 ring-blue-500' : ''
    } ${data.isInHydrationPlan === false ? 'opacity-70' : ''}`}
    style={{
      minWidth: '180px',
      backdropFilter: 'blur(8px)',
      boxShadow: selected ? 
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 0 0 2px rgba(59, 130, 246, 0.5)' : 
        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    }}>
      <Handle 
        type="target" 
        position={targetPosition} 
        className="w-2 h-2 !bg-gray-400"
        id={data.layoutDirection === 'BT' ? 'bottom' : 'top'} 
        style={{
          border: '2px solid',
          borderColor: darkMode ? '#374151' : '#E5E7EB',
          background: getHandleColor(data.status)
        }}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {getStatusIcon()}
          <span className="ml-2 font-mono text-sm truncate" title={data.path}>
            {data.label}
          </span>
        </div>
        {/* Add status badge */}
        <span className={`text-xs px-1.5 py-0.5 rounded-full ml-2 ${getStatusBadgeClass(data.status, darkMode)}`}>
          {data.status}
        </span>
      </div>
      <Handle 
        type="source" 
        position={sourcePosition} 
        className="w-2 h-2 !bg-gray-400"
        id={data.layoutDirection === 'BT' ? 'top' : 'bottom'} 
        style={{
          border: '2px solid',
          borderColor: darkMode ? '#374151' : '#E5E7EB',
          background: getHandleColor(data.status)
        }}
      />
    </div>
  );
};

export default FileNode;
